// Helpers for the public V3 API (`/api/public/v3`), which is JSON:API. Everything else in this app
// talks to `/api/v3` and gets flat JSON, so these exist to bridge the two conventions.

// Imported rather than read off the `appConstants` global: this module loads (via the actions and
// collections) before globals.js assigns window.appConstants.
import appConstants from 'constants/AppConstants';

export const publicV3Url = path => (
  appConstants.ApiPaths.MAKESHIFT_BASE_URL_DEV + appConstants.ApiPaths.API_PUBLIC_V3_PATH + path
);

const JSONAPI_CONTENT_TYPE = 'application/vnd.api+json';

// Sent per-request rather than added to Utils.setMakeshiftTokenHeader's global $.ajaxSetup: that
// beforeSend skips its Authorization injection entirely when options.headers.Authorization is set,
// so anything put on a request's own headers must leave Authorization alone.
//
// X-Localize-Times adds a `<attribute>_in_zone` beside every timestamp, rendered in the record's
// own zone. Shift requirements carry no time_zone attribute of their own (they delegate it to the
// department), so without this there is no way to show a shift at its local wall clock.
export const JSONAPI_HEADERS = {
  Accept: JSONAPI_CONTENT_TYPE,
  'X-Localize-Times': 'true',
};

// page[size] tops out at 100.
const PAGE_SIZE = 100;

// A row arrives as {id, type, attributes: {...}}; the rest of the app expects a flat object.
// `id` stays the string JSON:API sends, which matches route params and stringifies into URLs fine.
// A bare object with no attributes (the N/A availability probe answers one) passes through as is.
const flattenResource = row => (
  row.attributes ? Object.assign({ id: row.id }, row.attributes) : row
);

// Handles a list (`data` is an array), a single resource (`data` is an object) and no body (204).
export const flattenPayload = payload => (
  payload && payload.data ? [].concat(payload.data).map(flattenResource) : []
);

// A JSON:API write body: {data: {type, attributes}}.
export const writeBody = (type, attributes) => JSON.stringify({
  data: { type, attributes },
});

// The JSON:API `errors` array off a failed jqXHR, or [] when the body was not one (a network
// failure, a proxy page). jQuery already parses application/vnd.api+json as JSON — its content-type
// check is a word match on "json" — so responseJSON is normally set; the parse is a fallback.
const errorsOf = (xhr) => {
  let body = xhr && xhr.responseJSON;
  if (!body && xhr && xhr.responseText) {
    try {
      body = JSON.parse(xhr.responseText);
    } catch (e) {
      body = null;
    }
  }
  return body && Array.isArray(body.errors) ? body.errors : [];
};

// Every call resolves — never rejects — with one of two shapes:
//   {ok: true, rows, included, meta}    rows are the flattened `data`, included the flattened
//                                       `?include=` sideload, meta the response's meta or {}
//   {ok: false, status, errors}         status is the HTTP status (0 for a network failure),
//                                       errors the parsed JSON:API errors array
// Reads the outcome from the success/error callbacks rather than the jqXHR promise:
// utils/BackboneAjaxCache replays a cache hit through options.success while the underlying promise
// still rejects.
const request = (options) => {
  // The Authorization header comes from Utils.setMakeshiftTokenHeader's global beforeSend, which
  // every fetch in the app arms just before it goes out. Armed here so no caller can forget it.
  Utils.setMakeshiftTokenHeader();

  const deferred = $.Deferred();

  Backbone.ajax(Object.assign({
    headers: JSONAPI_HEADERS,
    success: response => deferred.resolve({
      ok: true,
      rows: flattenPayload(response),
      included: response && response.included ? response.included.map(flattenResource) : [],
      meta: (response && response.meta) || {},
    }),
    error: xhr => deferred.resolve({
      ok: false, status: xhr ? xhr.status : 0, errors: errorsOf(xhr),
    }),
  }, options));

  return deferred.promise();
};

// Nested filters rely on jQuery's default $.param bracket encoding, so never traditional: true.
export const jsonApiGet = (url, data) => request({ url, type: 'GET', data });

// Page one carries meta.total, so the remaining pages go out together rather than in sequence.
export const jsonApiGetAll = (url, data) => {
  const page = number => jsonApiGet(url, Object.assign({}, data, {
    page: { number, size: PAGE_SIZE },
  }));

  return page(1).then((first) => {
    if (!first.ok) {
      return first;
    }

    const total = typeof first.meta.total === 'number' ? first.meta.total : 0;
    const pageCount = Math.ceil(total / PAGE_SIZE);
    if (pageCount <= 1) {
      return first;
    }

    return $.when(...(_.range(2, pageCount + 1).map(page))).then((...rest) => {
      const failed = _.find(rest, result => !result.ok);
      return failed || Object.assign({}, first, {
        rows: rest.reduce((rows, result) => rows.concat(result.rows), first.rows),
      });
    });
  });
};

// `body` is writeBody's output, or absent for a DELETE or a bodiless PUT; Content-Type still goes
// out either way because the PUTs 415 without it. dataType: 'json' makes jQuery treat a 204 as
// "nocontent" and skip the JSON parse, so it lands in success with no response.
export const jsonApiWrite = (url, type, body) => request({
  url, type, contentType: JSONAPI_CONTENT_TYPE, data: body, dataType: 'json',
});
